// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Copy, IdCard, Loader2, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { type CreateVendorResult, useCreateVendorMutation } from "@/api/hooks/use-vendors";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

function createVendorSchema(t: (key: string) => string) {
  return z.object({
    name: z
      .string()
      .min(1, t("vendors.form.validation.required"))
      .max(32, t("vendors.form.validation.nameMaxLength"))
      .regex(/^[a-z0-9_]+$/, t("vendors.form.validation.nameFormat")),
    chainName: z.string().min(1, t("vendors.form.validation.required")),
  });
}

type CreateVendorValues = z.infer<ReturnType<typeof createVendorSchema>>;

interface CreateVendorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateVendorDialog({ open, onOpenChange }: CreateVendorDialogProps) {
  const { t } = useTranslation("back-office");
  const { data: context } = useBackofficeContext();
  const createVendor = useCreateVendorMutation();

  const schema = useMemo(() => createVendorSchema(t), [t]);
  const form = useForm<CreateVendorValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", chainName: "" },
  });

  // Holds the created credentials; switches the dialog from the form to the one-time reveal view.
  const [result, setResult] = useState<CreateVendorResult | null>(null);

  const prevOpen = useRef(false);
  useEffect(
    function resetStateOnDialogOpen() {
      if (open && !prevOpen.current) {
        form.reset();
        setResult(null);
      }
      prevOpen.current = open;
    },
    [open, form],
  );

  async function onSubmit(values: CreateVendorValues) {
    try {
      const created = await createVendor.mutateAsync({ name: values.name, chainName: values.chainName });
      toast.success(t("vendors.notifications.created", { name: values.name }));
      setResult(created);
    } catch {
      // Error toast handled by API middleware
    }
  }

  const created = !!result;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-vendor"
        className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={IdCard} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>
                    {created ? t("vendors.dialog.createdTitle") : t("vendors.dialog.createTitle")}
                  </DialogTitle>
                </DialogHeader>

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("vendors.form.name")} />
                      <FormControl>
                        <Input
                          autoComplete="off"
                          data-1p-ignore
                          data-lpignore="true"
                          maxLength={32}
                          disabled={created}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="chainName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("vendors.form.chain")} />
                      <FormControl>
                        <VirtualSelectPopover
                          value={field.value}
                          onChange={field.onChange}
                          options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                          placeholder={t("vendors.form.selectChain")}
                          searchPlaceholder={t("vendors.form.searchChains")}
                          emptyText={t("vendors.form.noChains")}
                          disabled={created}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {result && (
                  <>
                    <Separator className="my-1" />
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="text-sm">{t("vendors.dialog.createdWarning")}</span>
                    </div>
                    <SecretField label={t("vendors.dialog.apiKeyLabel")} value={result.apiKey} />
                    <SecretField label={t("vendors.dialog.privateKeyLabel")} value={result.privateKey} />
                  </>
                )}
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              {created ? (
                <Button type="button" onClick={() => onOpenChange(false)}>
                  {t("buttons.done")}
                </Button>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    {t("buttons.cancel")}
                  </Button>
                  <Button type="submit" disabled={createVendor.isPending}>
                    {createVendor.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <IdCard className="h-4 w-4" />
                    )}
                    {t("buttons.create")}
                  </Button>
                </>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/** Read-only monospace field for a generated secret, with a copy-to-clipboard button. */
function SecretField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation("back-office");
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast(
      <span>
        {t("common.copiedToClipboard")} <b className="break-all">{label}</b>
      </span>,
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="relative">
        <Input readOnly value={value} className="pr-9 font-mono text-xs" onFocus={(e) => e.target.select()} />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
          tabIndex={-1}
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
